import axios from 'axios';

// Create axios instance with base configuration
const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'https://sps.evoomedical.ro/api',
    timeout: 10000,
    headers: {
        'Content-Type': 'application/json',
        'Authorization': process.env.REACT_APP_API_TOKEN ? `Bearer ${process.env.REACT_APP_API_TOKEN}` : undefined,
    },
});

// Request interceptor to add auth token
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor to handle common errors
api.interceptors.response.use(
    (response) => {
        return response;
    },
    (error) => {
        if (error.response?.status === 401) {
            // Token expired or invalid
            localStorage.removeItem('authToken');
            localStorage.removeItem('user');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// Authentication API
export const authAPI = {
    register: (userData) => api.post('/auth/register', userData),
    login: (credentials) => api.post('/auth/login', credentials),
    forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
};

// Specialties API
export const specialtiesAPI = {
    getAll: () => api.get('/specialties'),
    getById: (id) => api.get(`/specialties/${id}`),
    getDoctorsBySpecialty: (id) => api.get(`/specialties/${id}/doctors`),
    create: (data) => api.post('/specialties', data),
    update: (id, data) => api.put(`/specialties/${id}`, data),
    delete: (id) => api.delete(`/specialties/${id}`),
};

// Doctors API
export const doctorsAPI = {
    getAll: () => api.get('/doctors'),
    getById: (id) => api.get(`/doctors/${id}`),
    getSchedule: (id) => api.get(`/doctors/${id}/schedule`),
    getAvailableSlots: (id, params) => api.get(`/doctors/${id}/available-slots`, { params }),
    update: (id, data) => api.put(`/doctors/${id}`, data),
};

// Appointments API
export const appointmentsAPI = {
    getAll: (params) => api.get('/appointments', { params }),
    getById: (id) => api.get(`/appointments/${id}`),
    create: (data) => api.post('/appointments', data),
    updateStatus: (id, data) => api.put(`/appointments/${id}/status`, data),
};

// Health check API
export const healthAPI = {
    check: () => api.get('/health'),
};

// Utility functions
export const setAuthToken = (token) => {
    if (token) {
        localStorage.setItem('authToken', token);
    } else {
        localStorage.removeItem('authToken');
    }
};

export const getAuthToken = () => {
    return localStorage.getItem('authToken');
};

export const setUser = (user) => {
    if (user) {
        localStorage.setItem('user', JSON.stringify(user));
    } else {
        localStorage.removeItem('user');
    }
};

export const getUser = () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
};

export const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    window.location.href = '/';
};

// Notifications API
export const notificationsAPI = {
    getTemplates: () => api.get('/notifications/templates'),
    sendEmail: (data) => api.post('/notifications/send', data),
    sendBulkEmail: (data) => api.post('/notifications/send-bulk', data),
    getStatus: () => api.get('/notifications/status'),
    testEmail: (data) => api.post('/notifications/test', data),
};

// SPS API (new stubbed APIs)
export const spsAPI = {
    // Doctors
    getDoctors: (params) => api.get('/v1/public/medici/list', { params }),
    getDoctorSchedule: (id) => api.get(`/v1/public/medici/${id}/orar`),
    getAvailableSchedules: (params) => api.get('/v1/public/medici/orar', { params }),

    // Cabinets
    getCabinets: (params) => api.get('/v1/public/cabinete/list', { params }),

    // Specialties
    getSpecialties: (params) => api.get('/v1/public/specialitati/list', { params }),

    // Appointments
    getAppointments: (params) => api.get('/v1/public/programari/list', { params }),
    createAppointment: (data) => api.post('/v1/public/programari/insert', data),
    editAppointment: (id, data) => api.post(`/v1/public/programari/edit/${id}`, data),
    getAppointmentDetails: (id) => api.get(`/v1/public/programari/edit/${id}`),
    cancelAppointment: (id, data) => api.post(`/v1/public/programari/anuleaza/${id}`, data),
    deleteAppointment: (id) => api.delete(`/v1/public/programari/delete/${id}`),

    // Patients
    getPatients: (params) => api.get('/v1/public/pacienti/list', { params }),
    createPatient: (data) => api.post('/v1/public/pacienti', data),
};

// Combined API service object for easier component usage
export const apiService = {
    // Specialties
    getSpecialties: async () => {
        const response = await specialtiesAPI.getAll();
        return { data: response.data.specialties || response.data };
    },
    getSpecialtyById: (id) => specialtiesAPI.getById(id),
    createSpecialty: (data) => specialtiesAPI.create(data),
    updateSpecialty: (id, data) => specialtiesAPI.update(id, data),
    deleteSpecialty: (id) => specialtiesAPI.delete(id),

    // Doctors
    getDoctors: async () => {
        const response = await doctorsAPI.getAll();
        return { data: response.data.doctors || response.data };
    },
    getDoctorById: (id) => doctorsAPI.getById(id),
    getDoctorsBySpecialty: async (specialtyId) => {
        const response = await specialtiesAPI.getDoctorsBySpecialty(specialtyId);
        return { data: response.data.doctors || response.data };
    },
    getDoctorSchedule: (id) => doctorsAPI.getSchedule(id),
    getAvailableSlots: async (doctorId, date) => {
        const response = await doctorsAPI.getAvailableSlots(doctorId, { date });
        return { data: response.data.availableSlots || response.data };
    },
    updateDoctor: (id, data) => doctorsAPI.update(id, data),

    // Appointments
    getAppointments: (params) => appointmentsAPI.getAll(params),
    getAppointmentById: (id) => appointmentsAPI.getById(id),
    createAppointment: (data) => appointmentsAPI.create(data),
    createPublicAppointment: async (data) => {
        const response = await api.post('/public/appointments', data);
        return response.data;
    },
    updateAppointmentStatus: (id, data) => appointmentsAPI.updateStatus(id, data),

    // Authentication
    register: (userData) => authAPI.register(userData),
    login: (credentials) => authAPI.login(credentials),
    forgotPassword: (email) => authAPI.forgotPassword(email),

    // Notifications
    getEmailTemplates: async () => {
        const response = await notificationsAPI.getTemplates();
        return { data: response.data.templates || response.data };
    },
    sendEmail: (data) => notificationsAPI.sendEmail(data),
    sendBulkEmail: (data) => notificationsAPI.sendBulkEmail(data),
    getNotificationStatus: () => notificationsAPI.getStatus(),
    testEmail: (data) => notificationsAPI.testEmail(data),

    // Health
    healthCheck: () => healthAPI.check(),

    // SPS APIs (new stubbed APIs)
    getSpsDoctors: async (params) => {
        const response = await spsAPI.getDoctors(params);
        return response.data;
    },
    getSpsDoctorSchedule: (id) => spsAPI.getDoctorSchedule(id),
    getSpsAvailableSchedules: async (params) => {
        const response = await spsAPI.getAvailableSchedules(params);
        return response.data;
    },
    getSpsCabinets: async (params) => {
        const response = await spsAPI.getCabinets(params);
        return response.data;
    },
    getSpsSpecialties: async (params) => {
        const response = await spsAPI.getSpecialties(params);
        return response.data;
    },
    getSpsAppointments: (params) => spsAPI.getAppointments(params),
    createSpsAppointment: (data) => spsAPI.createAppointment(data),
    editSpsAppointment: (id, data) => spsAPI.editAppointment(id, data),
    getSpsAppointmentDetails: (id) => spsAPI.getAppointmentDetails(id),
    cancelSpsAppointment: (id, data) => spsAPI.cancelAppointment(id, data),
    deleteSpsAppointment: (id) => spsAPI.deleteAppointment(id),
    getSpsPatients: (params) => spsAPI.getPatients(params),
    createSpsPatient: (data) => spsAPI.createPatient(data),
};

export default api;
